import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import PageHead from "@/components/PageHead";

// Compact, reusable primitives — kept local so this page carries no shared design surface.
const Section = ({ number, title, children }) => (
  <section
    className="border-t border-white/5 first:border-t-0"
    data-testid={`privacy-section-${number}`}
  >
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-14">
      <p className="label-mono text-terracotta">SECTION · {String(number).padStart(2, "0")}</p>
      <h2 className="font-serif text-2xl sm:text-3xl leading-tight text-bone mt-5">{title}</h2>
      <div className="mt-6 space-y-4 max-w-2xl text-base text-white/60 leading-relaxed">
        {children}
      </div>
    </div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-base text-white/60 leading-relaxed marker:text-terracotta/70">
    {items.map((it) => (
      <li key={it}>{it}</li>
    ))}
  </ul>
);

const EmailLink = ({ testid }) => (
  <a
    href="mailto:info@chronovaults.co.uk"
    className="text-bone/90 hover:text-terracotta transition-colors underline decoration-terracotta/40 underline-offset-4"
    data-testid={testid}
  >
    info@chronovaults.co.uk
  </a>
);

export default function Privacy() {
  return (
    <>
      <PageHead
        title="Privacy Policy | ChronoVaults"
        description="How ChronoVaults collects, uses and protects the personal information you provide when creating scheduled video and personal messages."
        canonical="https://chronovaults.co.uk/privacy"
      />
    <div className="relative" data-testid="privacy-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-20 pb-16">
          <div className="animate-fade-in">
            <p className="label-mono text-terracotta" data-testid="privacy-eyebrow">
              LEGAL · PRIVACY POLICY
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-bone mt-8"
              data-testid="privacy-heading"
            >
              Privacy <em className="text-terracotta not-italic font-light">Policy</em>
            </h1>
            <p
              className="label-mono text-white/50 mt-6"
              data-testid="privacy-last-updated"
            >
              LAST UPDATED · 12 SEPTEMBER 2026
            </p>

            <div className="mt-8 space-y-5 max-w-2xl">
              <p className="text-lg text-white/70 leading-relaxed">
                ChronoVaults respects your privacy and is committed to protecting your personal
                information.
              </p>
              <p className="text-base text-white/60 leading-relaxed">
                This Privacy Policy explains how we collect, use, store and protect personal
                information when you use the ChronoVaults website and services.
              </p>
              <p className="text-base text-white/60 leading-relaxed">
                ChronoVaults is based in the United Kingdom.
              </p>
              <p className="text-base text-white/60 leading-relaxed">
                For privacy-related questions, you can contact us at:{" "}
                <EmailLink testid="privacy-hero-email" />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <Section number={1} title="Information we collect">
        <p>We may collect information that you provide directly to us, including:</p>
        <Bullets
          items={[
            "your name",
            "email address",
            "telephone number",
            "account and login information",
            "billing and subscription information",
            "information you provide when creating or managing a ChronoVault",
            "recipient contact information that you provide so that a message can be delivered",
            "verification information used to help confirm a recipient's identity",
            "communications you send to us for support or enquiries",
          ]}
        />
        <p>
          ChronoVaults also processes the content that you choose to upload or create, including
          messages, videos and associated delivery instructions.
        </p>
        <p>
          We may also collect technical information automatically when you use the service, such
          as IP address, browser or device information, security logs and information necessary
          to maintain the reliability and security of the platform.
        </p>
      </Section>

      <Section number={2} title="How we use your information">
        <p>
          We use personal information where necessary to provide and operate the ChronoVaults
          service, including to:
        </p>
        <Bullets
          items={[
            "create and manage user accounts",
            "store and schedule ChronoVault messages",
            "deliver messages at the time selected by the sender",
            "send email and SMS notifications",
            "verify recipient access",
            "process payments and subscriptions",
            "provide customer support",
            "prevent fraud, misuse and unauthorised access",
            "monitor and maintain the security, reliability and performance of the service",
            "comply with legal and regulatory obligations",
          ]}
        />
        <p className="text-bone/90">We do not sell your personal information.</p>
      </Section>

      <Section number={3} title="Our lawful bases for processing">
        <p>
          Depending on the circumstances, we process personal information under one or more of
          the following lawful bases:
        </p>
        <dl className="space-y-4">
          <div>
            <dt className="font-serif text-bone text-lg">Contract</dt>
            <dd className="text-white/60 mt-1">
              where processing is necessary to provide the ChronoVaults service that you have
              requested.
            </dd>
          </div>
          <div>
            <dt className="font-serif text-bone text-lg">Legitimate interests</dt>
            <dd className="text-white/60 mt-1">
              where necessary to operate, secure, improve and protect the service, provided those
              interests are not overridden by your rights and interests.
            </dd>
          </div>
          <div>
            <dt className="font-serif text-bone text-lg">Legal obligation</dt>
            <dd className="text-white/60 mt-1">
              where we are required to process information to comply with applicable law.
            </dd>
          </div>
          <div>
            <dt className="font-serif text-bone text-lg">Consent</dt>
            <dd className="text-white/60 mt-1">
              where we specifically ask for your consent for a particular use of information.
            </dd>
          </div>
        </dl>
        <p>Where processing is based on consent, you may withdraw that consent at any time.</p>
      </Section>

      <Section number={4} title="Recipient information">
        <p>
          A ChronoVault sender may provide us with information about another person, such as
          their email address or telephone number, so that we can notify and verify the intended
          recipient.
        </p>
        <p>
          In these circumstances, we receive that information from the sender rather than
          directly from the recipient.
        </p>
        <p>
          We use recipient information only as necessary to deliver, secure and provide access to
          the relevant ChronoVault and for related service communications.
        </p>
      </Section>

      <Section number={5} title="Payments">
        <p>Payments are processed through third-party payment providers, including Stripe.</p>
        <p>ChronoVaults does not store full payment-card details.</p>
        <p>
          Payment providers may process personal and payment information in accordance with their
          own privacy policies and legal obligations.
        </p>
      </Section>

      <Section number={6} title="Email and SMS communications">
        <p>
          ChronoVaults uses third-party service providers to send transactional email and SMS
          communications.
        </p>
        <p>
          These communications may include account notifications, verification codes, delivery
          notifications and other messages necessary to operate the service.
        </p>
      </Section>

      <Section number={7} title="Who we share information with">
        <p>
          We may share personal information with trusted service providers where necessary to
          operate ChronoVaults, including providers of:
        </p>
        <Bullets
          items={[
            "cloud infrastructure and hosting",
            "data storage",
            "email delivery",
            "SMS delivery",
            "payment processing",
            "security and authentication services",
            "technical support and monitoring",
          ]}
        />
        <p>
          These providers are permitted to process information only as necessary to provide their
          services to us and subject to appropriate contractual and security protections.
        </p>
        <p>
          We may also disclose information where required by law, court order or lawful request
          from a competent authority, or where necessary to protect the rights, security or
          integrity of ChronoVaults, our users or others.
        </p>
      </Section>

      <Section number={8} title="International transfers">
        <p>
          Some of our service providers may process or store personal information outside the
          United Kingdom.
        </p>
        <p>
          Where personal information is transferred internationally, we take steps designed to
          ensure that appropriate safeguards are in place in accordance with applicable UK
          data-protection law.
        </p>
        <p>
          These safeguards may include adequacy regulations, recognised contractual protections
          such as the UK International Data Transfer Agreement or UK Addendum to approved
          Standard Contractual Clauses, or other legally recognised transfer mechanisms.
        </p>
        <p>
          You may contact us at <EmailLink testid="privacy-transfers-email" /> if you would like
          further information about the safeguards used for international transfers.
        </p>
      </Section>

      <Section number={9} title="How long we keep information">
        <p>
          We retain personal information only for as long as reasonably necessary for the
          purposes for which it was collected, including providing the service, maintaining
          security and records, resolving disputes and meeting legal or regulatory requirements.
        </p>
        <p>Retention periods may vary depending on the type of information and the reason it is held.</p>
        <p>
          ChronoVault content may be retained while it remains scheduled, available to the user
          or otherwise required to provide the service.
        </p>
        <p>
          Where an account or content is deleted, some information may remain temporarily in
          backups or records where necessary for security, fraud prevention, legal or technical
          reasons.
        </p>
      </Section>

      <Section number={10} title="Security">
        <p>
          We use technical and organisational measures designed to protect personal information
          against unauthorised access, loss, misuse, alteration or disclosure.
        </p>
        <p>
          These measures include access controls, authentication, secure communications and
          recipient-verification processes.
        </p>
        <p>
          No internet-based service can guarantee absolute security, but we continually work to
          protect the information entrusted to us.
        </p>
      </Section>

      <Section number={11} title="Your data-protection rights">
        <p>
          Depending on the circumstances, UK data-protection law may give you rights including:
        </p>
        <Bullets
          items={[
            "the right to access personal information we hold about you",
            "the right to have inaccurate information corrected",
            "the right to request deletion of your information",
            "the right to restrict certain processing",
            "the right to object to certain processing",
            "the right to data portability in applicable circumstances",
            "the right to withdraw consent where processing is based on consent",
          ]}
        />
        <p>Some rights are subject to legal exceptions and may not apply in every situation.</p>
        <p>
          To exercise your rights, contact: <EmailLink testid="privacy-rights-email" />
        </p>
        <p>We may need to verify your identity before completing certain requests.</p>
      </Section>

      <Section number={12} title="Cookies and similar technologies">
        <p>
          ChronoVaults may use cookies or similar technologies that are necessary for
          authentication, security and operation of the website.
        </p>
        <p>
          If we introduce analytics, advertising or other non-essential cookies, we will provide
          appropriate information and consent controls where required.
        </p>
      </Section>

      <Section number={13} title="Children">
        <p>
          ChronoVaults is intended for adults and is not designed for use by children.
        </p>
        <p>We do not knowingly encourage children to create ChronoVaults accounts.</p>
        <p>
          If you believe a child's personal information has been provided to us improperly,
          please contact us.
        </p>
      </Section>

      <Section number={14} title="Automated decision-making">
        <p>
          ChronoVaults does not currently make decisions about individuals based solely on
          automated processing that produce legal or similarly significant effects.
        </p>
      </Section>

      <Section number={15} title="Changes to this Privacy Policy">
        <p>
          We may update this Privacy Policy from time to time to reflect changes to the service,
          our providers, legal requirements or our privacy practices.
        </p>
        <p>
          The latest version will always be available on the ChronoVaults website and will show
          the date it was last updated.
        </p>
      </Section>

      <Section number={16} title="Contact us">
        <p>For privacy questions, requests or concerns:</p>
        <address className="not-italic pl-6 border-l-2 border-terracotta/40 py-1 my-2 space-y-1">
          <div className="font-serif text-lg text-bone">ChronoVaults</div>
          <div className="text-white/70">United Kingdom</div>
          <div className="text-white/70">
            Email: <EmailLink testid="privacy-contact-email" />
          </div>
        </address>
      </Section>

      <Section number={17} title="Complaints">
        <p>
          If you have concerns about how we use your personal information, please contact us
          first so that we can try to resolve them.
        </p>
        <p>
          You also have the right to complain to the UK supervisory authority:
        </p>
        <div className="pl-6 border-l-2 border-terracotta/40 py-1 my-2 space-y-1">
          <div className="font-serif text-lg text-bone">Information Commissioner's Office (ICO)</div>
          <div className="text-white/70">
            The ICO provides information about data-protection rights and how to make a complaint
            through its website.
          </div>
        </div>
      </Section>

      {/* CONTACT / LEGAL FOOTER (matches About page footer) */}
      <footer
        className="border-t border-white/10 bg-vault-950"
        data-testid="privacy-footer"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-3">
              <div
                className="flex items-center gap-2 text-sm text-white/60"
                data-testid="privacy-footer-location"
              >
                <MapPin className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span>ChronoVaults is based in the United Kingdom</span>
              </div>
              <a
                href="mailto:info@chronovaults.co.uk"
                className="flex items-center gap-2 text-sm text-bone/80 hover:text-terracotta transition-colors"
                data-testid="privacy-footer-email"
              >
                <Mail className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span>info@chronovaults.co.uk</span>
              </a>
            </div>

            <nav
              className="flex items-center gap-6 text-xs label-mono"
              data-testid="privacy-footer-legal"
              aria-label="Legal"
            >
              <Link
                to="/about"
                className="text-white/60 hover:text-bone transition-colors"
                data-testid="privacy-footer-about"
              >
                About
              </Link>
              <span className="text-white/20">·</span>
              <Link
                to="/terms"
                className="text-white/60 hover:text-bone transition-colors"
                data-testid="privacy-footer-terms"
              >
                Terms
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
